<script setup lang="ts">
   import { computed } from 'vue';
   import type { Tournament } from '~/types/types';
   import {
      isClosedForEntry,
      paymentCtaLabel,
      showPaymentLink,
      tournamentPayment,
   } from '~/functions/tournamentEntry';

   /**
    * The secondary "pay the entry fee" route on a brand's own tournament listing.
    *
    * Deliberately not used by PublicTournamentsTable: that table is shared by every brand
    * and by the embed widget, so the fee is offered on the brand's own pages instead. It
    * lives in a component rather than being written inline so that the brands adopting it
    * cannot drift apart - the same reason the helpers it calls live in
    * functions/tournamentEntry. It renders nothing when the tournament has no payment
    * link, so it is safe to drop into any card.
    *
    * The link deliberately outlives the entry CTA. There is no captain-facing email
    * carrying the payment URL, so this is the only route back to it once the tab is
    * closed, and hiding it the moment registration closes would strand anyone who
    * entered and came back to pay later.
    *
    * @click.stop on the anchor is kept for hosts whose rows or cards are themselves
    * clickable.
    */
   const props = defineProps<{ tournament: Tournament }>();

   const payment = computed(() => tournamentPayment(props.tournament));
   const isVisible = computed(() => showPaymentLink(props.tournament));
   const afterClose = computed(() => isClosedForEntry(props.tournament));

   // The conditional phrasing after close stops someone who never entered from paying
   // and assuming that put them in the tournament.
   const linkText = computed(() =>
      afterClose.value
         ? 'Already entered? Pay your entry fee'
         : paymentCtaLabel(payment.value),
   );
</script>

<template>
   <div v-if="isVisible" class="text-center">
      <a
         :href="payment?.url"
         :data-testid="`tournament-payment-link-${tournament.id}`"
         target="_blank"
         rel="noopener noreferrer"
         class="text-xs font-semibold text-brand-secondary underline underline-offset-2 transition-opacity hover:opacity-80"
         @click.stop>
         {{ linkText }}
      </a>

      <p v-if="payment?.amount_label" class="mt-0.5 text-xs text-gray-500">
         {{ payment.amount_label }}
      </p>
   </div>
</template>
